import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getSoftwareLicenses } from '../../../services/masterDataService';

const LicenseSelector = ({ selectedLicenses, onLicensesChange }) => {
  const [masterLicenses, setMasterLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [durations, setDurations] = useState({});
  const prevSigRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync local quantities/durations when selectedLicenses is replaced (e.g. loading a saved estimate)
  useEffect(() => {
    const currentSig = (selectedLicenses || [])
      ?.map(l => `${l?.masterId}:${l?.quantity ?? 1}:${l?.duration ?? 1}`)
      ?.sort()
      ?.join('|');
    if (prevSigRef?.current !== null && prevSigRef?.current !== currentSig) {
      const newQuantities = {};
      const newDurations = {};
      (selectedLicenses || [])?.forEach(l => {
        if (l?.masterId) {
          newQuantities[l.masterId] = l?.quantity ?? 1;
          newDurations[l.masterId] = l?.duration ?? 1;
        }
      });
      setQuantities(newQuantities);
      setDurations(newDurations);
    }
    prevSigRef.current = currentSig;
  }, [selectedLicenses]);

  useEffect(() => {
    const fetchLicenses = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getSoftwareLicenses();
        // Normalize DB records to the shape expected by the calculator
        const normalized = (data || [])?.map(lic => {
          const isPerp = lic?.licenseType === 'perpetual';
          const monthlyCost = isPerp ? 0 : (lic?.licenseType === 'annual' ? parseFloat((lic?.cost / 12)?.toFixed(2)) : lic?.cost);
          return {
            ...lic,
            type: isPerp ? 'perpetual' : 'subscription',
            monthlyCost,
            oneTimeCost: isPerp ? lic?.cost : 0,
          };
        });
        setMasterLicenses(normalized);
      } catch (err) {
        console.error('Failed to load licenses:', err);
        setError('Failed to load licenses from master data.');
      } finally {
        setLoading(false);
      }
    };
    fetchLicenses();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredLicenses = masterLicenses?.filter(l =>
    l?.softwareName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    l?.vendor?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    l?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const isSelected = (id) => selectedLicenses?.some(l => l?.masterId === id);

  const handleToggle = (license) => {
    if (isSelected(license?.id)) {
      onLicensesChange(selectedLicenses?.filter(l => l?.masterId !== license?.id));
    } else {
      const qty = parseInt(quantities?.[license?.id]) || 1;
      const dur = parseInt(durations?.[license?.id]) || 1;
      const entry = {
        id: Date.now() + Math.random(),
        masterId: license?.id,
        software: license?.id,
        softwareLabel: license?.softwareName,
        vendor: license?.vendor || '—',
        quantity: qty,
        duration: dur,
        monthlyCost: license?.monthlyCost || 0,
        oneTimeCost: license?.oneTimeCost || 0,
        type: license?.type,
      };
      onLicensesChange([...selectedLicenses, entry]);
    }
  };

  const updateSelected = (licenseId, patch) => {
    const idx = selectedLicenses?.findIndex(l => l?.masterId === licenseId);
    if (idx >= 0) {
      const updated = [...selectedLicenses];
      updated[idx] = { ...updated?.[idx], ...patch };
      onLicensesChange(updated);
    }
  };

  const handleQuantityChange = (licenseId, val) => {
    setQuantities(prev => ({ ...prev, [licenseId]: val }));
    updateSelected(licenseId, { quantity: parseInt(val) || 1 });
  };

  const handleDurationChange = (licenseId, val) => {
    setDurations(prev => ({ ...prev, [licenseId]: val }));
    updateSelected(licenseId, { duration: parseInt(val) || 1 });
  };

  const handleRemoveLicense = (id) => {
    onLicensesChange(selectedLicenses?.filter(l => l?.id !== id));
  };

  const calculateLicenseCost = (license) => {
    if (license?.type === 'perpetual') {
      return (license?.oneTimeCost || 0) * (license?.quantity || 1);
    }
    return (license?.monthlyCost || 0) * (license?.quantity || 1) * (license?.duration || 1);
  };

  const selectedCount = selectedLicenses?.length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-foreground">Required Licenses</label>
        {error && (
          <span className="text-xs text-error flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {error}
          </span>
        )}
      </div>
      {/* Searchable Dropdown Trigger */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen(prev => !prev)}
          disabled={loading}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all disabled:opacity-60"
        >
          <span className={selectedCount === 0 ? 'text-muted-foreground' : ''}>
            {loading ? 'Loading licenses...' : selectedCount === 0 ? 'Select licenses...' : `${selectedCount} license${selectedCount > 1 ? 's' : ''} selected`}
          </span>
          {loading
            ? <Icon name="Loader2" size={16} className="text-muted-foreground animate-spin" />
            : <Icon name={dropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
          }
        </button>

        {dropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search licenses..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground"
                  autoFocus
                />
              </div>
            </div>

            {/* Items List */}
            <div className="max-h-52 overflow-y-auto">
              {filteredLicenses?.length === 0 && (
                <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                  {masterLicenses?.length === 0 ? 'No licenses in master data. Add them in Master Data Management.' : 'No licenses found'}
                </div>
              )}
              {filteredLicenses?.map(license => {
                const checked = isSelected(license?.id);
                const selEntry = selectedLicenses?.find(l => l?.masterId === license?.id);
                return (
                  <div key={license?.id} className="px-3 py-2 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`lic-cb-${license?.id}`}
                        checked={checked}
                        onChange={() => handleToggle(license)}
                        className="w-4 h-4 rounded border-border text-primary accent-primary cursor-pointer"
                      />
                      <label htmlFor={`lic-cb-${license?.id}`} className="flex-1 cursor-pointer">
                        <div className="text-sm font-medium text-foreground">{license?.softwareName}</div>
                        <div className="text-xs text-muted-foreground">
                          {license?.vendor} · {license?.type === 'perpetual' ? `OMR ${parseFloat(license?.oneTimeCost || 0)?.toFixed(3)} one-time` : `OMR ${parseFloat(license?.monthlyCost || 0)?.toFixed(3)}/mo`}
                        </div>
                      </label>
                    </div>
                    {checked && (
                      <div className="mt-2 ml-6 flex gap-2">
                        <div className="flex-1">
                          <label className="text-xs text-muted-foreground mb-0.5 block">Qty</label>
                          <input
                            type="number"
                            min="1"
                            value={quantities?.[license?.id] ?? selEntry?.quantity ?? 1}
                            onChange={e => handleQuantityChange(license?.id, e?.target?.value)}
                            className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                          />
                        </div>
                        {license?.type !== 'perpetual' && (
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-0.5 block">Months</label>
                            <input
                              type="number"
                              min="1"
                              value={durations?.[license?.id] ?? selEntry?.duration ?? 1}
                              onChange={e => handleDurationChange(license?.id, e?.target?.value)}
                              className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Info footer */}
            <div className="border-t border-border px-3 py-2 bg-muted/20">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="Info" size={11} />
                Manage licenses in <span className="font-medium text-primary">Master Data Management</span>
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Selected Items List */}
      {selectedLicenses?.length > 0 ? (
        <div className="space-y-2">
          {selectedLicenses?.map((license) => (
            <div
              key={license?.id}
              className="flex items-start justify-between gap-2 p-3 bg-card border border-border rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{license?.softwareLabel}</div>
                <div className="text-xs text-muted-foreground mt-1 break-words">
                  {license?.type === 'perpetual'
                    ? `${license?.quantity} × OMR ${parseFloat(license?.oneTimeCost || 0)?.toFixed(3)} (one-time)`
                    : `${license?.quantity} × OMR ${parseFloat(license?.monthlyCost || 0)?.toFixed(3)}/mo × ${license?.duration} months`
                  }
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="text-sm font-semibold text-foreground whitespace-nowrap">
                  OMR {calculateLicenseCost(license)?.toFixed(3)}
                </div>
                <button
                  onClick={() => handleRemoveLicense(license?.id)}
                  className="text-error hover:text-error/80 transition-smooth"
                  aria-label="Remove license"
                >
                  <Icon name="Trash2" size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground text-sm">
          No licenses selected yet. Use the dropdown above to select licenses.
        </div>
      )}
    </div>
  );
};

export default LicenseSelector;